import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Spinner } from '@/components/ui';
import type { Form, FormField } from '../../../services/formService';
import { updateHiredEquipmentLines } from '../../../services/hiredEquipmentService';
import type { Task } from '../../../types/Task';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  canApproveSubmissionForForm,
  canEditDailyLogForForm,
} from '../../forms/utils/submissionPermissions';
import { useHiredEquipmentGridData } from '../hooks/useHiredEquipmentGridData';
import { usePoBalanceWarning } from '../hooks/usePoBalanceWarning';
import type { HiredEquipmentGridRow } from '../types';
import {
  heAnchorFromForm,
  heGridPersistPayload,
  heGridSubmitRows,
  parseHeGridRows,
} from '../utils/heSummaryRows';
import { validateGridRows } from '../utils/gridValidation';
import {
  appendMissingPoResources,
  duplicateRow,
  emptyRowFromAnchor,
  lumpSumRowFromAnchor,
  resolveLockedPoId,
} from '../utils/poPrefill';
import { defaultFuelSurchargeEligibleFromVehicleType } from '../utils/fuelSurchargeDefault';
import { hiredEquipmentPoService } from '../../../services/hiredEquipmentPoService';
import FormPictures from '../../forms/components/FormPictures';
import HiredEquipmentGrid from './HiredEquipmentGrid';
import HiredEquipmentTaskPictures from './HiredEquipmentTaskPictures';
import PoBalanceWarningModal from './PoBalanceWarningModal';
import type { HiredEquipmentAnchor } from '../types';
import { getHiredEquipmentInternalComment } from '../utils/internalComment';
import HiredEquipmentInternalComment from './HiredEquipmentInternalComment';

interface HiredEquipmentDetailedViewProps {
  form: Form;
  availableTasks: Task[];
  onFieldsReplace?: (formId: string, fields: FormField[]) => void;
  onResubmitted?: (formId: string) => void | Promise<void>;
}

const HiredEquipmentDetailedView: React.FC<HiredEquipmentDetailedViewProps> = ({
  form,
  availableTasks,
  onFieldsReplace,
  onResubmitted,
}) => {
  const { hasPermission } = usePermissions();
  const status = (form.status || '').toLowerCase();
  const needsRevisions = status === 'need revisions';
  const statusAllowsEdit =
    status === 'pending' || status === 'revisions completed' || needsRevisions;
  const canEdit =
    statusAllowsEdit &&
    (canEditDailyLogForForm(hasPermission, form) || canApproveSubmissionForForm(hasPermission, form));

  const baseAnchor = useMemo(() => heAnchorFromForm(form.fields), [form.fields]);
  const [rows, setRows] = useState<HiredEquipmentGridRow[]>(() => parseHeGridRows(form.fields, availableTasks));
  const [internalComment, setInternalComment] = useState(() =>
    getHiredEquipmentInternalComment(form.fields),
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [hasResubmitted, setHasResubmitted] = useState(false);
  const lastSavedRef = useRef(
    JSON.stringify({
      rows: heGridPersistPayload(parseHeGridRows(form.fields, availableTasks)),
      internalComment: getHiredEquipmentInternalComment(form.fields),
    }),
  );
  const skipHydrateSaveRef = useRef(true);
  const waitingOnRevisions = needsRevisions && !hasResubmitted;

  const lookups = useHiredEquipmentGridData(baseAnchor, rows);
  const poBalanceWarning = usePoBalanceWarning();

  const anchor = useMemo((): HiredEquipmentAnchor => {
    const lockedPoId = resolveLockedPoId(baseAnchor, rows, lookups.purchaseOrders);
    if (lockedPoId == null) return baseAnchor;
    return { ...baseAnchor, lockedPoId };
  }, [baseAnchor, rows, lookups.purchaseOrders]);

  useEffect(() => {
    setRows(parseHeGridRows(form.fields, availableTasks));
    const nextInternalComment = getHiredEquipmentInternalComment(form.fields);
    setInternalComment(nextInternalComment);
    lastSavedRef.current = JSON.stringify({
      rows: heGridPersistPayload(parseHeGridRows(form.fields, availableTasks)),
      internalComment: nextInternalComment,
    });
    skipHydrateSaveRef.current = true;
  }, [form.id]);

  useEffect(() => {
    setHasResubmitted(false);
  }, [form.id, form.status]);

  useEffect(() => {
    setRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        let updated = row;
        if (updated.vendorId == null && updated.vendorName) {
          const vendor = lookups.vendors.find(
            (item) => item.name.trim().toLowerCase() === updated.vendorName!.trim().toLowerCase(),
          );
          if (vendor) {
            updated = { ...updated, vendorId: vendor.id };
            changed = true;
          }
        }
        if (updated.vendorId != null && updated.poId == null && updated.poNumber) {
          const po = lookups.purchaseOrders.find(
            (item) => item.poNumber === updated.poNumber && item.vendorId === updated.vendorId,
          );
          if (po) {
            updated = { ...updated, poId: po.id };
            changed = true;
          }
        }
        // A lump sum has no catalogue resource; its label is literally "Lump Sum",
        // so a vendor unit of that name would otherwise be matched onto it here.
        if (!updated.lumpSum && updated.resourceId == null && updated.resourceLabel) {
          const resource = lookups.resourcesForRow(updated).find((item) => item.label === updated.resourceLabel);
          if (resource) {
            updated = {
              ...updated,
              resourceId: resource.id,
              unit: resource.unit || updated.unit,
              vehicleType: resource.vehicleType ?? updated.vehicleType ?? null,
              fuelSurchargeEligible:
                updated.fuelSurchargeEligible ??
                defaultFuelSurchargeEligibleFromVehicleType(resource.vehicleType),
            };
            changed = true;
          }
        }
        return updated;
      });
      return changed ? next : prev;
    });
  }, [lookups.vendors, lookups.purchaseOrders, lookups.resourcesForRow]);

  useEffect(() => {
    if (!canEdit) return;
    if (skipHydrateSaveRef.current) {
      skipHydrateSaveRef.current = false;
      return;
    }
    const payload = heGridPersistPayload(rows);
    const serialized = JSON.stringify({ rows: payload, internalComment });
    if (serialized === lastSavedRef.current) return;
    const complete = rows.filter((row) => row.date || row.taskId || row.qty != null || row.rate != null || row.resourceLabel);
    if (!validateGridRows(complete).ok) return;

    const handle = window.setTimeout(() => {
      const persistRows = heGridSubmitRows(
        complete.filter((row) => row.taskId != null && row.qty != null && row.rate != null && row.date),
      );
      if (persistRows.length === 0) return;
      setSaveState('saving');
      setSaveError(null);
      void updateHiredEquipmentLines(form.id, { rows: persistRows, internalComment })
        .then((result) => {
          lastSavedRef.current = JSON.stringify({
            rows: heGridPersistPayload(rows),
            internalComment,
          });
          setSaveState('saved');
          onFieldsReplace?.(form.id, result.fields);
        })
        .catch((error: unknown) => {
          setSaveState('idle');
          setSaveError(error instanceof Error ? error.message : 'Failed to save lines');
        });
    }, 500);
    return () => window.clearTimeout(handle);
  }, [rows, internalComment, canEdit, form.id, onFieldsReplace]);

  const handleAddLine = () => setRows((prev) => [...prev, emptyRowFromAnchor(anchor)]);
  const handleAddLumpSumLine = () => setRows((prev) => [...prev, lumpSumRowFromAnchor(anchor)]);

  const resolvedPoId = anchor.lockedPoId ?? null;
  const handleDuplicate = (clientId: string) => {
    setRows((prev) => {
      const source = prev.find((row) => row.clientId === clientId);
      if (!source) return prev;
      return [...prev, duplicateRow(source)];
    });
  };
  const handleRemove = (clientId: string) => setRows((prev) => prev.filter((row) => row.clientId !== clientId));
  const handleReAddPoResources = async () => {
    if (resolvedPoId == null) return;
    const resources = await hiredEquipmentPoService.getPoResources(resolvedPoId);
    setRows((prev) => appendMissingPoResources(anchor, prev, resources));
  };

  const handleResubmit = async (options?: { skipPoWarning?: boolean }) => {
    const complete = rows.filter(
      (row) => row.date || row.taskId || row.qty != null || row.rate != null || row.resourceLabel,
    );
    const validation = validateGridRows(complete);
    if (!validation.ok) {
      setSaveError('Fix incomplete lines before resubmitting.');
      return;
    }
    const persistRows = heGridSubmitRows(
      complete.filter((row) => row.taskId != null && row.qty != null && row.rate != null && row.date),
    );
    if (persistRows.length === 0) {
      setSaveError('Add at least one complete line before resubmitting.');
      return;
    }
    if (!options?.skipPoWarning) {
      const okToSubmit = await poBalanceWarning.check({
        rows: complete,
        lockedPoId: resolvedPoId,
        purchaseOrders: lookups.purchaseOrders,
        fuelSurcharges: lookups.fuelSurcharges,
      });
      if (!okToSubmit) return;
    }
    setSaveState('saving');
    setSaveError(null);
    try {
      const result = await updateHiredEquipmentLines(form.id, {
        rows: persistRows,
        internalComment,
        resubmit: true,
      });
      lastSavedRef.current = JSON.stringify({
        rows: heGridPersistPayload(rows),
        internalComment,
      });
      setSaveState('saved');
      setHasResubmitted(true);
      onFieldsReplace?.(form.id, result.fields);
      await onResubmitted?.(form.id);
    } catch (error: unknown) {
      setSaveState('idle');
      setSaveError(error instanceof Error ? error.message : 'Failed to resubmit');
    }
  };

  return (
    <div>
      {waitingOnRevisions && (
        <Alert variant="warning" className="tw-mb-3">
          This submission was sent back and is waiting on you. Fix the lines below
          {form.statusComments ? ` — ${form.statusComments}` : ''}. Changes save as you type. Use{' '}
          <strong>Resubmit</strong> when you are done so it returns to the approval queue — not a new draft.
        </Alert>
      )}
      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
          {canEdit
            ? waitingOnRevisions
              ? 'Correct the lines that were sent back. Resubmit when the fix is complete.'
              : 'Edit lines below before approving — correct quantities, rates, add or remove lines. Changes save as you type.'
            : 'Line items for this submission.'}
        </p>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          {canEdit && saveState === 'saving' && (
            <span className="tw-text-xs tw-text-content-muted">Saving…</span>
          )}
          {canEdit && saveState === 'saved' && !saveError && (
            <span className="tw-text-xs tw-text-success">Saved</span>
          )}
          {waitingOnRevisions && canEdit && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleResubmit()}
              disabled={saveState === 'saving' || poBalanceWarning.checking}
            >
              {saveState === 'saving' ? (
                <>
                  <Spinner as="span" size="sm" className="tw-mr-1" />
                  Resubmitting…
                </>
              ) : poBalanceWarning.checking ? (
                'Checking…'
              ) : (
                'Resubmit'
              )}
            </Button>
          )}
        </div>
      </div>
      {saveError && (
        <Alert variant="danger" className="tw-mb-3">
          {saveError}
        </Alert>
      )}
      <HiredEquipmentGrid
        anchor={anchor}
        rows={rows}
        onRowsChange={setRows}
        vendors={lookups.vendors}
        taskOptionForRow={lookups.taskOptionForRow}
        searchTasks={lookups.searchTasks}
        purchaseOrders={lookups.purchaseOrders}
        tasksByPoId={lookups.tasksByPoId}
        resourcesForRow={lookups.resourcesForRow}
        fuelSurcharges={lookups.fuelSurcharges}
        onAddLine={handleAddLine}
        onAddLumpSumLine={handleAddLumpSumLine}
        onDuplicateLine={handleDuplicate}
        onRemoveLine={handleRemove}
        onReAddPoResources={anchor.entryMethod === 'purchase_order' ? handleReAddPoResources : undefined}
        readOnly={!canEdit}
      />
      <HiredEquipmentInternalComment
        value={internalComment}
        onChange={canEdit ? setInternalComment : undefined}
        className="tw-mt-3"
      />
      <HiredEquipmentTaskPictures formId={form.id} rows={rows} />
      <PoBalanceWarningModal
        show={poBalanceWarning.warnings != null}
        warnings={poBalanceWarning.warnings ?? []}
        confirmLabel="Resubmit anyway"
        onCancel={poBalanceWarning.dismiss}
        onConfirm={() => {
          poBalanceWarning.dismiss();
          void handleResubmit({ skipPoWarning: true });
        }}
      />
    </div>
  );
};

export default HiredEquipmentDetailedView;
